import { BaseApiClient } from './BaseApiClient';
import { IApiInterface } from './IApiConfig';
import { IAuthTokenBuilder } from './IAuthTokenBuilder';

export class WorkoutSelectionClient extends BaseApiClient {
    private logger: any;

    public constructor(config: IApiInterface, fetch: any, logger: any, authTokenBuilder: IAuthTokenBuilder|undefined) {
        super(config, fetch, authTokenBuilder);
        this.logger = logger || (() => {
            // empty
        });
    }

    public getBasicAuthUserAndRoles = async () => {
      this.logger(`[WorkoutSelectionClient] authenticating and getting basic auth user`);
      const request = await this.buildGetRequest('api/users/me');
      const result = await this.fetch(
        request.url,
        this.buildFetchOptionsWithCredentialsInclude(request.options));
      const data = await this.assertHttp200AndGetJsonFromResult(result);
      return data;
    }

    public getAllWorkouts = async () => {
        this.logger(`[WorkoutSelectionClient] get all workouts`);
        const request = await this.buildGetRequest('api/workout-selection/');
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public filterWorkouts = async (status: any, from: number, to: number) => {
        this.logger(`[WorkoutSelectionClient] get filtered workouts for status ${status}`);
        const url = `api/workout-selection/?status=${status}&from=${from}&to=${to}`;
        const request = await this.buildGetRequest(url);
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public getWorkoutSelection = async (id: string) => {
        this.logger(`[WorkoutSelectionClient] get workout selection`);
        const request = await this.buildGetRequest(`api/workout-selection/${id}`);
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public getFilteredExercises = async (
        workoutSetNumber: number, workoutSettings: any, gymType: any) => {
        this.logger(`[WorkoutSelectionClient] filter exercises`);
        const request = await this.buildPostRequest(`api/workout-selection/filter`, {
            workoutSetNumber,
            workoutSettings,
            gymType,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public makeWorkoutActive = async (
        workoutSetNumber: number, workoutSettings: any, gymType: any) => {
        this.logger(`[WorkoutSelectionClient] set active workout`);
        const request = await this.buildPostRequest(`api/live/settings/active-workout-selection`, {
            workoutSetNumber,
            workoutSettings,
            gymType,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public generateWorkoutCopyFromSettings = async (
        workoutSetNumber: number, fromWorkoutSettings: any,
        toWorkoutSettings: any, workoutType: number, gymType: any) => {
        this.logger(`[WorkoutSelectionClient] regenerate and store workout`);
        const request = await this.buildPostRequest(`api/workout-selection/?mode=copybasicfromintermediate`, {
            workoutSetNumber,
            fromWorkoutSettings,
            toWorkoutSettings,
            workoutType,
            gymType,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public copyWorkout3rdAnd4thExercises = async (
        workoutSetNumberToCopyFrom: number, workoutSetNumber: number, workoutSettings: any,
        workoutType: number, gymType: any) => {
        this.logger(`[WorkoutSelectionClient] regenerate and store workout`);
        const request = await this.buildPostRequest(`api/workout-selection/?mode=copyWorkout3rdAnd4thExercises`, {
            workoutSetNumberToCopyFrom,
            workoutSetNumber,
            workoutSettings,
            workoutType,
            gymType,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public regenerateAndStoreWorkoutSelection = async (
        workoutSetNumber: number, workoutSettings: any, gymType: any) => {
        this.logger(`[WorkoutSelectionClient] regenerate and store workout`);
        const request = await this.buildPostRequest(`api/workout-selection?mode=regenerate`, {
            workoutSetNumber,
            workoutSettings,
            gymType,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }

    public update = async (workoutSelection: any) => {
        this.logger(`[WorkoutSelectionClient] update workout selection`);
        const request = await this.buildPutRequest('api/workout-selection', {
            workoutSelection,
        });
        const result = await this.fetch(
            request.url,
            this.buildFetchOptionsWithCredentialsInclude(request.options));
        const data = await this.assertHttp200AndGetJsonFromResult(result);
        return data;
    }
}
